'use client';
import TabComponent from '@/componentv2/tab/tab';
import { GetAllCategoriesResult } from '@/sanity.types';
import './getInspiredCaseStudies.scss';
import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Button from '@/componentv2/button/button';
import FontAwesomeIcon from '@/componentv2/fontAwesomeIcon/fontAwesomeIcon';
import { useRouter } from 'next/navigation';

interface GetInspiredProps {
  tabs: GetAllCategoriesResult;
}

const GetInspired = ({ tabs }: GetInspiredProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const router = useRouter();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleVisibility = () => {
    setIsVisible((prev) => !prev);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleMobileTabClick = (href: string, e: React.MouseEvent) => {
    e.preventDefault();
    router.push(href);
    setIsVisible(false); // Close dropdown
    // Small delay to ensure navigation happens before scroll
    setTimeout(() => {
      scrollToTop();
    }, 100);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef?.current &&
        !dropdownRef?.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  return (
    <div className="getInspiredWrap">
      <h2>Get inspired</h2>
      <div className="badge desktop-only">
        <span>Popular topics</span>
      </div>
      <div className="mobile-dropdown">
        <div className="caseStudy-category-dropdown" ref={dropdownRef}>
          <Button
            onClick={toggleVisibility}
            aria-haspopup="true"
            aria-expanded={isVisible}
            aria-controls="caseStudy-dropdown"
            className="caseStudy-type-btn"
          >
            Popular topics
            <FontAwesomeIcon
              name="fa-chevron-down"
              style="fa-solid"
              fontSize="14px"
            />
          </Button>
          <div
            className={`caseStudyCateDropdown ${isVisible ? 'show' : 'hide'}`}
            id="caseStudy-dropdown"
            role="menu"
            aria-labelledby="caseStudy-dropdown"
          >
            {tabs
              ?.filter((category) => category?.title !== 'All')
              .map((category) => {
                const href =
                  `/case-studies/category/${category?.slug?.current}` || '#';
                return (
                  <Link
                    key={category?._id}
                    role="menuitem"
                    href={href}
                    onClick={(e) => handleMobileTabClick(href, e)}
                  >
                    {category?.title}
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
      <div className="desktop-tabs">
        <TabComponent tabs={tabs} mainURL="/case-studies" />
      </div>
    </div>
  );
};

export default GetInspired;
